/**
 * GasesView.js
 *
 * Copyright (c) 2026 Eric Seigne <eric.seigne@cap-rel.fr>
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <https://www.gnu.org/licenses/>.
 */

import { Gas } from '../core/Gas.js'
import { Store } from '../store/Store.js'
import { escapeHtml } from '../utils/viewUtils.js'
import { CCR } from '../core/CCR.js'
import { i18n } from '../i18n/index.js'

export class GasesView {
  constructor(container) {
    this.container = container
    this.editingGasId = null
    this.editingDiluentId = null
    // Store subscription refreshing the MOD and END when the dive site
    // changes, dropped by destroy()
    this.unsubscribe = null
    // Environment this view last drew its depths for
    this.environmentKey = null
  }

  t(key, params) {
    return i18n.t(key, params)
  }

  // Dive environment (altitude / water type) of the current plan. Every depth
  // <-> pressure conversion of this view goes through it, so a MOD or an END
  // displayed here is the one the planner uses for the same mix.
  environment() {
    return window.store?.state?.environment || {}
  }

  // What the displayed depths depend on. gasTemp is in there because it feeds
  // the gas density shown next to the mixes, not only the water and altitude.
  static environmentSignature(env = {}) {
    return `${env.waterType ?? 'salt'}|${env.altitude ?? 0}|${env.gasTemp ?? 35}`
  }

  // Redraw when the dive site changes somewhere else.
  //
  // On the desktop dashboard this view sits next to the plan view, so the water
  // or the altitude can be changed in the neighbouring column while this one is
  // on screen. Without this, a MOD of 21 m would stay put after switching to
  // fresh water, where the very same mix reaches 22 m.
  //
  // Only an environment change redraws, and never while a dialog is open: this
  // view rebuilds its whole markup, which would tear the field being typed into
  // out of the DOM. environmentKey is deliberately left untouched in that case,
  // so the redraw happens on the next notification instead of being lost.
  subscribeToEnvironment() {
    if (this.unsubscribe) return
    this.unsubscribe = window.store.subscribe(() => {
      if (!this.container) return
      const key = GasesView.environmentSignature(this.environment())
      if (key === this.environmentKey) return
      if (this.editingGasId !== null || this.editingDiluentId !== null) return
      this.render()
    })
  }

  // MOD and END at that MOD for an open circuit mix, computed by the core.
  // The fractions come either from the store or from a form field currently
  // being typed into, so an incomplete or aberrant mix is an expected state:
  // it is logged and reported as { mod: null, end: null } so the caller shows
  // a neutral value instead of a wrong depth - and, above all, so the live
  // update of the modal is not frozen by a PlanError.
  ocMixDepths(fO2, fHe, ppO2Max) {
    if (!Number.isFinite(ppO2Max) || ppO2Max <= 0) {
      console.error('GasesView: invalid ppO2 limit (%o) for mix fO2=%o fHe=%o', ppO2Max, fO2, fHe)
      return { mod: null, end: null }
    }
    const env = this.environment()
    try {
      Gas.validate({ fO2, fHe })
      const mod = Gas.calcMOD(fO2, ppO2Max, env)
      return { mod, end: Gas.calcEND(mod, fO2, fHe, env) }
    } catch (e) {
      console.error('GasesView: unusable gas mix fO2=%o fHe=%o ppO2Max=%o (%s)', fO2, fHe, ppO2Max, e.message)
      return { mod: null, end: null }
    }
  }

  // MOD and END at that MOD for a CCR diluent breathed at `setpoint`.
  // END is delegated to CCR.calcEND, the very function the plan table uses, so
  // the same diluent cannot show two different narcotic depths any more.
  diluentDepths(diluent, ppO2Max, setpoint) {
    if (!Number.isFinite(ppO2Max) || ppO2Max <= 0 || !Number.isFinite(setpoint) || setpoint <= 0) {
      console.error('GasesView: invalid ppO2 limit (%o) or setpoint (%o) for diluent %o',
        ppO2Max, setpoint, diluent?.name)
      return { mod: null, end: null }
    }
    const env = this.environment()
    try {
      const mod = Gas.calcMOD(diluent.fO2, ppO2Max, env)
      return { mod, end: CCR.calcEND(mod, setpoint, diluent, env) }
    } catch (e) {
      console.error('GasesView: unusable diluent %o fO2=%o fHe=%o (%s)',
        diluent?.name, diluent?.fO2, diluent?.fHe, e.message)
      return { mod: null, end: null }
    }
  }

  destroy() {
    // The store subscription outlives the view: left in place it would redraw
    // a detached container at every state change.
    if (this.unsubscribe) {
      this.unsubscribe()
      this.unsubscribe = null
    }
    // Nothing else to unwire: no timer, and no listener outside this view's
    // container. Every listener it installs is bound to a node produced by
    // render() (the gas and diluent dialogs are part of container.innerHTML),
    // so they are released together with the container content.
    this.container = null
    this.editingGasId = null
    this.editingDiluentId = null
  }

  render() {
    const { gases, diluents, ccr } = window.store.state

    this.container.innerHTML = `
      <div class="space-y-4 pb-20">
        <!-- Diluents (CCR only). On a rebreather the diluent is the gas being
             breathed, the bailout only comes into play on a failure: it comes
             first here. In open circuit this block renders nothing, so the gas
             card stays at the top of the page. -->
        ${ccr.enabled ? `
        <div class="card bg-base-100 shadow-sm">
          <div class="card-body p-4">
            <div class="flex justify-between items-center">
              <h2 class="card-title text-sm">${this.t('gases_diluents')}</h2>
              <button id="add-diluent-btn" class="btn btn-primary btn-xs">+ ${this.t('add')}</button>
            </div>

            <div id="diluent-list" class="space-y-2 mt-2">
              ${diluents.slice().sort((a, b) => b.fO2 - a.fO2).map(d => this.renderDiluentCard(d)).join('')}
            </div>
          </div>
        </div>
        ` : ''}

        <!-- Bailout / OC Gases -->
        <div class="card bg-base-100 shadow-sm">
          <div class="card-body p-4">
            <div class="flex justify-between items-center">
              <h2 class="card-title text-sm">${ccr.enabled ? this.t('gases_bailout') : this.t('gases_title')}</h2>
              <button id="add-gas-btn" class="btn btn-primary btn-xs">+ ${this.t('add')}</button>
            </div>

            <div id="gas-list" class="space-y-2 mt-2">
              ${gases.slice().sort((a, b) => b.fO2 - a.fO2).map(g => this.renderGasCard(g)).join('')}
            </div>
          </div>
        </div>
      </div>

      <!-- Add/Edit Gas Modal -->
      <dialog id="gas-modal" class="modal">
        <div class="modal-box">
          <h3 class="font-bold text-lg" id="gas-modal-title">${this.t('gases_title')}</h3>

          <div class="form-control mt-4">
            <label class="label cursor-pointer justify-start gap-2 py-1">
              <input type="checkbox" id="gas-auto-label" class="checkbox checkbox-sm checkbox-primary" checked />
              <span class="label-text">${this.t('gases_auto_label')}</span>
            </label>
            <input type="text" id="gas-name" class="input input-bordered" placeholder="Auto" />
          </div>

          <div class="grid grid-cols-2 gap-4 mt-2">
            <div class="form-control">
              <label class="label"><span class="label-text">O2 %</span></label>
              <input type="number" id="gas-o2" min="5" max="100" value="21" class="input input-bordered" />
            </div>
            <div class="form-control">
              <label class="label"><span class="label-text">He %</span></label>
              <input type="number" id="gas-he" min="0" max="95" value="0" class="input input-bordered" />
            </div>
          </div>

          <div class="form-control mt-2">
            <label class="label"><span class="label-text">${this.t('gases_n2_calc')}</span></label>
            <input type="number" id="gas-n2" class="input input-bordered bg-base-200" readonly />
          </div>

          <div class="form-control mt-2">
            <label class="label"><span class="label-text">${this.t('gases_ppo2_max')} (bar)</span></label>
            <input type="number" id="gas-ppo2" min="0.8" max="1.6" step="0.1" value="1.4" class="input input-bordered" />
          </div>

          <!-- Real-time MOD/END display -->
          <div class="grid grid-cols-2 gap-4 mt-4">
            <div class="stat bg-base-200 rounded-lg p-3">
              <div class="stat-title text-xs">${this.t('gases_mod')}</div>
              <div class="stat-value text-lg" id="calc-mod">--</div>
            </div>
            <div class="stat bg-base-200 rounded-lg p-3">
              <div class="stat-title text-xs">${this.t('gases_end_at_mod')}</div>
              <div class="stat-value text-lg" id="calc-end">--</div>
            </div>
          </div>

          <div class="form-control mt-4">
            <label class="label cursor-pointer">
              <span class="label-text">${this.t('gases_use_bailout')}</span>
              <input type="checkbox" id="gas-bailout" class="checkbox checkbox-primary" checked />
            </label>
          </div>

          <!-- Tank configuration -->
          <div class="divider text-xs">${this.t('gases_tank_config')}</div>
          <div class="grid grid-cols-2 gap-4">
            <div class="form-control">
              <label class="label"><span class="label-text">${this.t('gases_volume')}</span></label>
              <input type="number" id="gas-tank-volume" min="1" max="50" step="0.1" value="12" class="input input-bordered" />
            </div>
            <div class="form-control">
              <label class="label"><span class="label-text">${this.t('gases_pressure')}</span></label>
              <input type="number" id="gas-tank-pressure" min="100" max="300" step="10" value="200" class="input input-bordered" />
            </div>
          </div>
          <div class="text-xs opacity-70 mt-2">
            ${this.t('gases_capacity')}: <span id="tank-capacity">2400</span> L
          </div>
          <div id="gas-form-error" class="text-error text-sm mt-2 hidden"></div>

          <div class="modal-action">
            <button id="cancel-gas-btn" class="btn btn-ghost">${this.t('cancel')}</button>
            <button id="save-gas-btn" class="btn btn-primary">${this.t('save')}</button>
          </div>
        </div>
        <form method="dialog" class="modal-backdrop"><button>close</button></form>
      </dialog>

      <!-- Add/Edit Diluent Modal -->
      <dialog id="diluent-modal" class="modal">
        <div class="modal-box">
          <h3 class="font-bold text-lg">${this.t('plan_diluent')}</h3>

          <div class="form-control mt-4">
            <label class="label cursor-pointer justify-start gap-2 py-1">
              <input type="checkbox" id="diluent-auto-label" class="checkbox checkbox-sm checkbox-primary" checked />
              <span class="label-text">${this.t('gases_auto_label')}</span>
            </label>
            <input type="text" id="diluent-name" class="input input-bordered" placeholder="Auto" />
          </div>

          <div class="grid grid-cols-2 gap-4 mt-2">
            <div class="form-control">
              <label class="label"><span class="label-text">O2 %</span></label>
              <input type="number" id="diluent-o2" min="5" max="21" value="21" class="input input-bordered" />
            </div>
            <div class="form-control">
              <label class="label"><span class="label-text">He %</span></label>
              <input type="number" id="diluent-he" min="0" max="90" value="0" class="input input-bordered" />
            </div>
          </div>

          <div class="form-control mt-2">
            <label class="label"><span class="label-text">${this.t('gases_n2_calc')}</span></label>
            <input type="number" id="diluent-n2" class="input input-bordered bg-base-200" readonly />
          </div>

          <div id="diluent-form-error" class="text-error text-sm mt-2 hidden"></div>

          <div class="modal-action">
            <button id="cancel-diluent-btn" class="btn btn-ghost">${this.t('cancel')}</button>
            <button id="save-diluent-btn" class="btn btn-primary">${this.t('save')}</button>
          </div>
        </div>
        <form method="dialog" class="modal-backdrop"><button>close</button></form>
      </dialog>
    `

    // Recorded before binding, so the subscription installed just below never
    // sees the environment it has just drawn as a change.
    this.environmentKey = GasesView.environmentSignature(this.environment())
    this.bindEvents()
    this.subscribeToEnvironment()
  }

  renderGasCard(gas) {
    const { mod, end } = this.ocMixDepths(gas.fO2, gas.fHe || 0, gas.ppO2Max || 1.4)

    return `
      <div class="flex items-center justify-between p-3 bg-base-200 rounded-lg" data-gas-id="${gas.id}">
        <div class="flex items-center gap-3">
          <input type="checkbox" class="toggle toggle-sm toggle-primary gas-toggle"
            data-id="${gas.id}" ${gas.enabled ? 'checked' : ''} />
          <div>
            <div class="font-medium">${escapeHtml(gas.name)}</div>
            <div class="text-xs opacity-70">
              O2: ${Math.round(gas.fO2 * 100)}% | He: ${Math.round((gas.fHe || 0) * 100)}% | N2: ${Math.round((1 - gas.fO2 - (gas.fHe || 0)) * 100)}%
            </div>
            <div class="text-xs opacity-70">
              ${this.t('gases_mod')}: ${mod !== null ? mod + 'm' : '--'} | ${this.t('gases_end')}: ${end !== null && end >= 0 ? end + 'm' : '-'} | ppO2: ${gas.ppO2Max || 1.4}
            </div>
            <div class="text-xs opacity-70">
              ${this.t('plan_tank')}: ${gas.tankVolume || 12}L @ ${gas.tankPressure || 200}bar (${(gas.tankVolume || 12) * (gas.tankPressure || 200)}L)
            </div>
          </div>
        </div>
        <div class="flex gap-1">
          <button class="btn btn-ghost btn-xs edit-gas-btn" data-id="${gas.id}">${this.t('edit')}</button>
          <button class="btn btn-ghost btn-xs text-error delete-gas-btn" data-id="${gas.id}">X</button>
        </div>
      </div>
    `
  }

  renderDiluentCard(diluent) {
    const isActive = diluent.enabled
    const { ccr } = window.store.state
    const setpoint = ccr.mode === 'fixed' ? ccr.fixedSetpoint : ccr.dynamicSetpoints.bottom
    // MOD for diluent based on its O2 fraction at max ppO2 (1.6 for deco)
    const ppO2Max = 1.6
    const { mod, end: endAtMod } = this.diluentDepths(diluent, ppO2Max, setpoint)

    return `
      <div class="flex items-center justify-between p-3 bg-base-200 rounded-lg ${isActive ? 'ring-2 ring-primary' : ''}"
           data-diluent-id="${diluent.id}">
        <div class="flex items-center gap-3">
          <input type="checkbox" class="toggle toggle-sm toggle-primary diluent-toggle"
            data-id="${diluent.id}" ${isActive ? 'checked' : ''} />
          <div>
            <div class="font-medium">${escapeHtml(diluent.name)}</div>
            <div class="text-xs opacity-70">
              O2: ${Math.round(diluent.fO2 * 100)}% | He: ${Math.round((diluent.fHe || 0) * 100)}% | N2: ${Math.round((1 - diluent.fO2 - (diluent.fHe || 0)) * 100)}%
            </div>
            <div class="text-xs opacity-70">
              ${this.t('gases_mod')}: ${mod !== null ? mod + 'm' : '--'} | ${this.t('gases_end')} @${this.t('gases_mod')} (SP${setpoint}): ${endAtMod !== null && endAtMod >= 0 ? endAtMod + 'm' : '-'}
            </div>
          </div>
        </div>
        <div class="flex gap-1">
          <button class="btn btn-ghost btn-xs edit-diluent-btn" data-id="${diluent.id}">${this.t('edit')}</button>
          <button class="btn btn-ghost btn-xs text-error delete-diluent-btn" data-id="${diluent.id}">X</button>
        </div>
      </div>
    `
  }

  bindEvents() {
    // Gas toggle
    this.container.querySelectorAll('.gas-toggle').forEach(toggle => {
      toggle.addEventListener('change', (e) => {
        window.store.toggleGas(parseInt(e.target.dataset.id))
      })
    })

    // Add gas
    document.getElementById('add-gas-btn')?.addEventListener('click', () => {
      this.openGasModal(null)
    })

    // Edit gas
    this.container.querySelectorAll('.edit-gas-btn').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const id = parseInt(e.target.dataset.id)
        this.openGasModal(id)
      })
    })

    // Delete gas
    this.container.querySelectorAll('.delete-gas-btn').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const id = parseInt(e.target.dataset.id)
        if (confirm(this.t('gases_confirm_delete'))) {
          window.store.removeGas(id)
          this.render()
        }
      })
    })

    // Gas modal inputs - real-time calculation
    const o2Input = document.getElementById('gas-o2')
    const heInput = document.getElementById('gas-he')
    const n2Input = document.getElementById('gas-n2')
    const ppo2Input = document.getElementById('gas-ppo2')
    const modDisplay = document.getElementById('calc-mod')
    const endDisplay = document.getElementById('calc-end')
    const nameInput = document.getElementById('gas-name')
    const autoLabelInput = document.getElementById('gas-auto-label')

    const applyAutoLabelState = () => {
      if (!nameInput || !autoLabelInput) return
      nameInput.readOnly = autoLabelInput.checked
      nameInput.classList.toggle('bg-base-200', autoLabelInput.checked)
    }

    const updateCalculations = () => {
      const fO2 = parseFloat(o2Input?.value || 21) / 100
      const fHe = parseFloat(heInput?.value || 0) / 100
      const fN2 = Math.max(0, 1 - fO2 - fHe)
      const ppO2 = parseFloat(ppo2Input?.value || 1.4)

      if (n2Input) n2Input.value = Number.isFinite(fN2) ? Math.round(fN2 * 100) : ''

      // These fields are read while the diver is still typing, so an
      // incomplete mix (0% O2, more than 100% O2, a half erased number) is a
      // normal transient state. It must never throw here: the whole live
      // update of MOD, END and automatic name would stop for good.
      const { mod, end } = this.ocMixDepths(fO2, fHe, ppO2)

      if (modDisplay) modDisplay.textContent = mod !== null ? `${mod}m` : '--'
      if (endDisplay) endDisplay.textContent = (end !== null && end >= 0) ? `${end}m` : '-'

      if (autoLabelInput?.checked && nameInput && Number.isFinite(fO2) && Number.isFinite(fHe)) {
        nameInput.value = Gas.formatAutoName(fO2, fHe)
      }
    }

    o2Input?.addEventListener('input', updateCalculations)
    heInput?.addEventListener('input', updateCalculations)
    ppo2Input?.addEventListener('input', updateCalculations)
    autoLabelInput?.addEventListener('change', () => {
      applyAutoLabelState()
      updateCalculations()
    })
    applyAutoLabelState()

    // Tank capacity calculation
    const tankVolumeInput = document.getElementById('gas-tank-volume')
    const tankPressureInput = document.getElementById('gas-tank-pressure')
    const tankCapacityDisplay = document.getElementById('tank-capacity')

    const updateTankCapacity = () => {
      const volume = parseFloat(tankVolumeInput?.value || 12)
      const pressure = parseFloat(tankPressureInput?.value || 200)
      if (tankCapacityDisplay) {
        tankCapacityDisplay.textContent = Math.round(volume * pressure)
      }
    }

    tankVolumeInput?.addEventListener('input', updateTankCapacity)
    tankPressureInput?.addEventListener('input', updateTankCapacity)

    // Initialize calculations
    updateCalculations()
    updateTankCapacity()

    // Save gas
    document.getElementById('save-gas-btn')?.addEventListener('click', () => {
      this.saveGas()
    })

    // Cancel gas
    document.getElementById('cancel-gas-btn')?.addEventListener('click', () => {
      document.getElementById('gas-modal').close()
    })

    // Diluent toggle
    this.container.querySelectorAll('.diluent-toggle').forEach(toggle => {
      toggle.addEventListener('change', (e) => {
        window.store.toggleDiluent(parseInt(e.target.dataset.id))
        this.render()
      })
    })

    // Add diluent
    document.getElementById('add-diluent-btn')?.addEventListener('click', () => {
      this.openDiluentModal(null)
    })

    // Edit diluent
    this.container.querySelectorAll('.edit-diluent-btn').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const id = parseInt(e.target.dataset.id)
        this.openDiluentModal(id)
      })
    })

    // Delete diluent. The removal goes through the store and not through a
    // direct write to state.diluents: on a rebreather the diluents are an input
    // of the calculation, and removing one also shifts the diluentIndex every
    // segment addresses its mix with, so a plan computed before the removal
    // must be dropped instead of being left on screen.
    this.container.querySelectorAll('.delete-diluent-btn').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const id = parseInt(e.target.dataset.id)
        if (window.store.state.diluents.length <= 1) {
          alert(this.t('gases_only_diluent'))
          return
        }
        if (confirm(this.t('gases_confirm_delete_diluent'))) {
          window.store.removeDiluent(id)
          this.render()
        }
      })
    })

    // Diluent modal inputs
    const dilO2 = document.getElementById('diluent-o2')
    const dilHe = document.getElementById('diluent-he')
    const dilN2 = document.getElementById('diluent-n2')
    const dilName = document.getElementById('diluent-name')
    const dilAutoLabel = document.getElementById('diluent-auto-label')

    const applyDilAutoLabelState = () => {
      if (!dilName || !dilAutoLabel) return
      dilName.readOnly = dilAutoLabel.checked
      dilName.classList.toggle('bg-base-200', dilAutoLabel.checked)
    }

    const updateDiluentN2 = () => {
      const fO2 = parseFloat(dilO2?.value || 21) / 100
      const fHe = parseFloat(dilHe?.value || 0) / 100
      const fN2 = Math.max(0, 1 - fO2 - fHe)
      // Same reasoning as updateCalculations: a half typed fraction must not
      // write NaN into the N2 field nor into the automatic diluent name.
      if (dilN2) dilN2.value = Number.isFinite(fN2) ? Math.round(fN2 * 100) : ''

      if (dilAutoLabel?.checked && dilName && Number.isFinite(fO2) && Number.isFinite(fHe)) {
        dilName.value = Gas.formatAutoNameDiluent(fO2, fHe)
      }
    }

    dilO2?.addEventListener('input', updateDiluentN2)
    dilHe?.addEventListener('input', updateDiluentN2)
    dilAutoLabel?.addEventListener('change', () => {
      applyDilAutoLabelState()
      updateDiluentN2()
    })
    applyDilAutoLabelState()
    updateDiluentN2()

    // Save diluent
    document.getElementById('save-diluent-btn')?.addEventListener('click', () => {
      this.saveDiluent()
    })

    // Cancel diluent
    document.getElementById('cancel-diluent-btn')?.addEventListener('click', () => {
      document.getElementById('diluent-modal').close()
    })
  }

  openGasModal(id) {
    const modal = document.getElementById('gas-modal')
    this.editingGasId = id

    if (id !== null) {
      const gas = window.store.state.gases.find(g => g.id === id)
      if (gas) {
        document.getElementById('gas-modal-title').textContent = 'Edit Gas'
        document.getElementById('gas-name').value = gas.name
        document.getElementById('gas-o2').value = Math.round(gas.fO2 * 100)
        document.getElementById('gas-he').value = Math.round((gas.fHe || 0) * 100)
        document.getElementById('gas-ppo2').value = gas.ppO2Max || 1.4
        document.getElementById('gas-bailout').checked = gas.isBailout !== false
        document.getElementById('gas-tank-volume').value = gas.tankVolume || 12
        document.getElementById('gas-tank-pressure').value = gas.tankPressure || 200
        // Auto-label is on when explicitly set, or when name still matches the auto format
        const autoLabel = gas.autoLabel !== undefined
          ? gas.autoLabel
          : gas.name === Gas.formatAutoName(gas.fO2, gas.fHe || 0)
        document.getElementById('gas-auto-label').checked = autoLabel
      }
    } else {
      document.getElementById('gas-modal-title').textContent = 'Add Gas'
      document.getElementById('gas-name').value = ''
      document.getElementById('gas-o2').value = 21
      document.getElementById('gas-he').value = 0
      document.getElementById('gas-ppo2').value = 1.4
      document.getElementById('gas-bailout').checked = true
      document.getElementById('gas-tank-volume').value = 12
      document.getElementById('gas-tank-pressure').value = 200
      document.getElementById('gas-auto-label').checked = true
    }

    // Trigger calculation updates and apply auto-label readonly state
    document.getElementById('gas-auto-label').dispatchEvent(new Event('change'))
    document.getElementById('gas-tank-volume').dispatchEvent(new Event('input'))

    modal.showModal()
  }

  saveGas() {
    const fO2 = parseFloat(document.getElementById('gas-o2').value) / 100
    const fHe = parseFloat(document.getElementById('gas-he').value) / 100
    const ppO2Max = parseFloat(document.getElementById('gas-ppo2').value)
    const isBailout = document.getElementById('gas-bailout').checked
    const tankVolume = parseFloat(document.getElementById('gas-tank-volume').value)
    const tankPressure = parseFloat(document.getElementById('gas-tank-pressure').value)
    const autoLabel = document.getElementById('gas-auto-label').checked

    let name = document.getElementById('gas-name').value.trim()
    if (autoLabel || !name) {
      name = Gas.formatAutoName(fO2, fHe)
    }

    const gasData = {
      name,
      fO2,
      fHe,
      ppO2Max,
      isBailout,
      tankVolume,
      tankPressure,
      autoLabel,
      enabled: true
    }

    // Checked here, with the reason shown under the form, and checked again
    // by the store: an emptied field parses to NaN, and a NaN mix or cylinder
    // used to be stored and refused at every calculation instead
    if (!this.acceptForm('gas-form-error', Store.gasInputProblem(gasData), gasData)) return

    const written = this.editingGasId !== null
      ? window.store.updateGas(this.editingGasId, gasData)
      : window.store.addGas(gasData)
    if (written === false || written === null) {
      this.acceptForm('gas-form-error', 'refused_by_store', gasData)
      return
    }

    document.getElementById('gas-modal').close()
    this.render()
  }

  // Show or clear the error line of a gas / diluent form. Returns true when
  // there is no problem and the form may be written.
  acceptForm(errorId, problem, data) {
    const el = document.getElementById(errorId)
    if (!problem) {
      el?.classList.add('hidden')
      return true
    }
    console.error('GasesView: form %s refused (%s) %o', errorId, problem, data)
    if (el) {
      el.textContent = this.t('gases_form_invalid')
      el.classList.remove('hidden')
    }
    return false
  }

  openDiluentModal(id) {
    const modal = document.getElementById('diluent-modal')
    this.editingDiluentId = id

    if (id !== null) {
      const diluent = window.store.state.diluents.find(d => d.id === id)
      if (diluent) {
        document.getElementById('diluent-name').value = diluent.name
        document.getElementById('diluent-o2').value = Math.round(diluent.fO2 * 100)
        document.getElementById('diluent-he').value = Math.round((diluent.fHe || 0) * 100)
        const autoLabel = diluent.autoLabel !== undefined
          ? diluent.autoLabel
          : diluent.name === Gas.formatAutoNameDiluent(diluent.fO2, diluent.fHe || 0)
        document.getElementById('diluent-auto-label').checked = autoLabel
      }
    } else {
      document.getElementById('diluent-name').value = ''
      document.getElementById('diluent-o2').value = 21
      document.getElementById('diluent-he').value = 0
      document.getElementById('diluent-auto-label').checked = true
    }

    // Trigger N2 calculation and apply auto-label readonly state
    document.getElementById('diluent-auto-label').dispatchEvent(new Event('change'))

    modal.showModal()
  }

  saveDiluent() {
    const fO2 = parseFloat(document.getElementById('diluent-o2').value) / 100
    const fHe = parseFloat(document.getElementById('diluent-he').value) / 100
    const autoLabel = document.getElementById('diluent-auto-label').checked

    let name = document.getElementById('diluent-name').value.trim()
    if (autoLabel || !name) {
      name = Gas.formatAutoNameDiluent(fO2, fHe)
    }

    const diluentData = { name, fO2, fHe, autoLabel }

    if (!this.acceptForm('diluent-form-error', Store.gasInputProblem(diluentData, { cylinder: false }), diluentData)) return

    const written = this.editingDiluentId !== null
      ? window.store.updateDiluent(this.editingDiluentId, diluentData)
      : window.store.addDiluent(diluentData)
    if (written === false || written === null) {
      this.acceptForm('diluent-form-error', 'refused_by_store', diluentData)
      return
    }

    document.getElementById('diluent-modal').close()
    this.render()
  }
}
